<template>
  <section
    class="projects-showcase"
    :class="{
      'show-hover-left': hoverState.side === 'left',
      'show-hover-right': hoverState.side === 'right'
    }"
    @mousemove="handleShowcaseHover"
    @mouseleave="clearHoverSide"
    @click="handleShowcaseClick"
  >
    <div class="container">
      <section
        v-for="subcategory in projectSubcategories"
        :key="subcategory.id"
        class="subcategory"
        :data-subcategory-id="subcategory.id"
      >
        <header class="subcategory-header">
          <h3 class="subcategory-title">{{ tr(subcategory.name) }}</h3>
        </header>

        <div class="carousel-viewport">
          <Transition :name="getTransitionName(subcategory.id)" mode="out-in">
            <article
              :key="getCurrentProject(subcategory.id).id"
              class="project-card"
            >
              <div class="project-top">
                <h4 class="project-title">{{ tr(getCurrentProject(subcategory.id).title) }}</h4>
                <img class="project-logo" src="@/assets/logo.svg" :alt="$t('projectsPage.logoAlt')" />
              </div>

              <div class="project-bottom">
                <div class="project-info">
                  <h5 class="info-title">{{ $t('projectsPage.infoTitle') }}</h5>
                  <ul class="info-list">
                    <li
                      v-for="(item, infoIndex) in getCurrentProject(subcategory.id).technicalInfo"
                      :key="`${getCurrentProject(subcategory.id).id}-info-${infoIndex}`"
                    >
                      {{ tr(item) }}
                    </li>
                  </ul>

                  <div
                    v-if="getCurrentProject(subcategory.id).links"
                    class="project-links"
                  >
                    <a
                      v-if="getCurrentProject(subcategory.id).links.hotel"
                      :href="getCurrentProject(subcategory.id).links.hotel"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="project-link-btn"
                    >
                      {{ $t('projectsPage.hotel') }}
                    </a>
                    <a
                      v-if="getCurrentProject(subcategory.id).links.rcdb"
                      :href="getCurrentProject(subcategory.id).links.rcdb"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="project-link-btn"
                    >
                      {{ $t('projectsPage.rcdb') }}
                    </a>
                  </div>
                </div>

                <div class="project-video">
                  <iframe
                    :src="toEmbedUrl(getCurrentProject(subcategory.id).youtubeEmbed)"
                    :title="$t('projectsPage.videoTitle', { title: tr(getCurrentProject(subcategory.id).title) })"
                    frameborder="0"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                </div>
              </div>

              <div
                v-if="getCurrentProject(subcategory.id).location"
                class="project-map"
              >
                <iframe
                  class="project-map-embed"
                  :src="toMapEmbedUrl(getCurrentProject(subcategory.id).location.coordinates)"
                  :title="$t('projectsPage.mapTitle', { title: tr(getCurrentProject(subcategory.id).title) })"
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                  allowfullscreen
                ></iframe>
              </div>
            </article>
          </Transition>

          <div
            class="carousel-controls"
            role="group"
            :aria-label="$t('projectsPage.carouselControls', { name: tr(subcategory.name) })"
          >
            <button
              type="button"
              :class="[
                'arrow-btn',
                'arrow-btn-prev',
                {
                  'is-area-active':
                    hoverState.side === 'left' && hoverState.subcategoryId === subcategory.id
                }
              ]"
              :aria-label="$t('projectsPage.previousProject')"
              @click.stop="previousProject(subcategory.id)"
            >
              &#8592;
            </button>
            <button
              type="button"
              :class="[
                'arrow-btn',
                'arrow-btn-next',
                {
                  'is-area-active':
                    hoverState.side === 'right' && hoverState.subcategoryId === subcategory.id
                }
              ]"
              :aria-label="$t('projectsPage.nextProject')"
              @click.stop="nextProject(subcategory.id)"
            >
              &#8594;
            </button>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup>
import { reactive } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, te } = useI18n()

const tr = (value) => (typeof value === 'string' && te(value) ? t(value) : value)

const omashuExpressVideo = 'https://www.youtube.com/watch?v=f59654RmbIc'
const oompamobile1Video = 'https://www.youtube.com/watch?v=Fv61MQ5k7no'
const oompamobile2Video = 'https://www.youtube.com/shorts/TEUZ0Y4jNuc'
const benazelartVideo = 'https://www.youtube.com/watch?v=02ex9hBiXNI'
const contratempoVideo = 'https://www.youtube.com/watch?v=QtOuFWwOsvU'
const eurekaVideo = 'https://www.youtube.com/shorts/N2pICVB5oh4'
const barcaVideo = 'https://www.youtube.com/watch?v=CWVt28DHskQ'
const arvoreHavanVideo = 'https://www.youtube.com/watch?v=zV1eHqX_tNs'
const araraVideo = 'https://www.youtube.com/shorts/XrukdLBvYYo'
const toboaguaVideo = 'https://www.youtube.com/shorts/iOgELvWVBb0'
const defaultVideoEmbed = 'https://www.youtube-nocookie.com/embed/4AJ_E2bV_cE'

const projectSubcategories = [
  {
    id: 'contratempo',
    name: 'projectsPage.contratempo',
    projects: [
      {
        id: 'oompamobile-1',
        title: 'projectsPage.projectTitles.oompamobile1',
        youtubeEmbed: oompamobile1Video,
        technicalInfo: [
          'projectsPage.techInfo.oompamobile1.status',
          'projectsPage.techInfo.oompamobile1.speed',
          'projectsPage.techInfo.oompamobile1.extension',
          'projectsPage.techInfo.oompamobile1.height',
          'projectsPage.techInfo.oompamobile1.type',
          'projectsPage.techInfo.oompamobile1.year'
        ],
        location: {
          coordinates: '-22.483223,-46.638888'
        },
        links: {
          hotel: 'https://www.benditocacaoresort.com.br/',
          rcdb: 'https://rcdb.com/22433.htm'
        }
      },
      {
        id: 'oompamobile-2',
        title: 'projectsPage.projectTitles.oompamobile2',
        youtubeEmbed: oompamobile2Video,
        technicalInfo: [
          'projectsPage.techInfo.oompamobile2.status',
          'projectsPage.techInfo.oompamobile2.speed',
          'projectsPage.techInfo.oompamobile2.extension',
          'projectsPage.techInfo.oompamobile2.height',
          'projectsPage.techInfo.oompamobile2.type',
          'projectsPage.techInfo.oompamobile2.year'
        ],
        location: {
          coordinates: '-22.69788,-45.564496'
        },
        links: {
          hotel: 'https://www.benditocacaoresort.com.br/',
          rcdb: 'https://rcdb.com/22982.htm'
        }
      },
      {
        id: 'benazelart',
        title: 'projectsPage.projectTitles.benazelart',
        youtubeEmbed: benazelartVideo,
        technicalInfo: [
          'projectsPage.techInfo.benazelart.status',
          'projectsPage.techInfo.benazelart.speed',
          'projectsPage.techInfo.benazelart.extension',
          'projectsPage.techInfo.benazelart.height',
          'projectsPage.techInfo.benazelart.type',
          'projectsPage.techInfo.benazelart.year'
        ],
        location: {
          coordinates: '33.833649,-118.1578311'
        }
      },
      {
        id: 'contratempo-1',
        title: 'projectsPage.projectTitles.contratempo1',
        youtubeEmbed: contratempoVideo,
        technicalInfo: [
          'projectsPage.techInfo.contratempo1.status',
          'projectsPage.techInfo.contratempo1.speed',
          'projectsPage.techInfo.contratempo1.extension',
          'projectsPage.techInfo.contratempo1.height',
          'projectsPage.techInfo.contratempo1.type',
          'projectsPage.techInfo.contratempo1.year'
        ],
        location: {
          coordinates: '-23.3738688,-46.8237135'
        }
      }
    ]
  },
  {
    id: 'eureka',
    name: 'projectsPage.eureka',
    projects: [
      {
        id: 'omashu-express',
        title: 'projectsPage.projectTitles.omashuExpress',
        youtubeEmbed: omashuExpressVideo,
        technicalInfo: [
          'projectsPage.techInfo.omashuExpress.status',
          'projectsPage.techInfo.omashuExpress.speed',
          'projectsPage.techInfo.omashuExpress.extension',
          'projectsPage.techInfo.omashuExpress.height',
          'projectsPage.techInfo.omashuExpress.type',
          'projectsPage.techInfo.omashuExpress.year'
        ],
        location: {
          coordinates: '-23.3738688,-46.8237135'
        }
      },
      {
        id: 'eureka-v1',
        title: 'projectsPage.projectTitles.eurekaV1',
        youtubeEmbed: eurekaVideo,
        technicalInfo: [
          'projectsPage.techInfo.eurekaV1.status',
          'projectsPage.techInfo.eurekaV1.speed',
          'projectsPage.techInfo.eurekaV1.extension',
          'projectsPage.techInfo.eurekaV1.height',
          'projectsPage.techInfo.eurekaV1.type',
          'projectsPage.techInfo.eurekaV1.year'
        ],
        location: {
          coordinates: '-23.3738688,-46.8237135'
        }
      }
    ]
  },
  {
    id: 'flat-rides',
    name: 'projectsPage.flatRides',
    projects: [
      {
        id: 'flat-viking',
        title: 'projectsPage.projectTitles.flatViking',
        youtubeEmbed: barcaVideo,
        technicalInfo: [
          'projectsPage.techInfo.flatViking.status',
          'projectsPage.techInfo.flatViking.type',
          'projectsPage.techInfo.flatViking.height',
          'projectsPage.techInfo.flatViking.year'
        ],
        location: {
          coordinates: '-23.3738688,-46.8237135'
        }
      },
      {
        id: 'arara',
        title: 'projectsPage.projectTitles.arara',
        youtubeEmbed: araraVideo,
        technicalInfo: [
          'projectsPage.techInfo.arara.status',
          'projectsPage.techInfo.arara.type',
          'projectsPage.techInfo.arara.height',
          'projectsPage.techInfo.arara.year'
        ],
        location: {
          coordinates: '-23.3738688,-46.8237135'
        }
      },
      {
        id: 'toboagua',
        title: 'projectsPage.projectTitles.toboagua',
        youtubeEmbed: toboaguaVideo,
        technicalInfo: [
          'projectsPage.techInfo.toboagua.status',
          'projectsPage.techInfo.toboagua.type',
          'projectsPage.techInfo.toboagua.height',
          'projectsPage.techInfo.toboagua.year'
        ],
        location: {
          coordinates: '-23.3738688,-46.8237135'
        }
      },
      {
        id: 'arvoreHavan',
        title: 'projectsPage.projectTitles.arvoreHavan',
        youtubeEmbed: arvoreHavanVideo,
        technicalInfo: [
          'projectsPage.techInfo.arvoreHavan.status',
          'projectsPage.techInfo.arvoreHavan.type',
          'projectsPage.techInfo.arvoreHavan.height',
          'projectsPage.techInfo.arvoreHavan.year'
        ],
        location: {
          coordinates: '-23.5238105,-46.7668797'
        },
        links: null
      }
    ]
  }
]

const activeProjectIndex = reactive(
  Object.fromEntries(projectSubcategories.map((subcategory) => [subcategory.id, 0]))
)

const transitionDirection = reactive(
  Object.fromEntries(projectSubcategories.map((subcategory) => [subcategory.id, 'next']))
)

const hoverState = reactive({
  side: null,
  subcategoryId: null
})

const getSubcategoryById = (subcategoryId) =>
  projectSubcategories.find((subcategory) => subcategory.id === subcategoryId)

const getCurrentProject = (subcategoryId) => {
  const subcategory = getSubcategoryById(subcategoryId)
  if (!subcategory || subcategory.projects.length === 0) {
    return {
      id: `${subcategoryId}-empty`,
      title: 'projectsPage.noProject',
      youtubeEmbed: defaultVideoEmbed,
      technicalInfo: ['projectsPage.addProjectsHint'],
      location: null,
      links: null
    }
  }

  return subcategory.projects[activeProjectIndex[subcategoryId]]
}

const previousProject = (subcategoryId) => {
  const subcategory = getSubcategoryById(subcategoryId)
  if (!subcategory || subcategory.projects.length <= 1) return
  transitionDirection[subcategoryId] = 'prev'
  const current = activeProjectIndex[subcategoryId]
  activeProjectIndex[subcategoryId] =
    (current - 1 + subcategory.projects.length) % subcategory.projects.length
}

const nextProject = (subcategoryId) => {
  const subcategory = getSubcategoryById(subcategoryId)
  if (!subcategory || subcategory.projects.length <= 1) return
  transitionDirection[subcategoryId] = 'next'
  activeProjectIndex[subcategoryId] =
    (activeProjectIndex[subcategoryId] + 1) % subcategory.projects.length
}

const getTransitionName = (subcategoryId) =>
  transitionDirection[subcategoryId] === 'prev' ? 'slide-prev' : 'slide-next'

const setHoverSide = (side) => {
  hoverState.side = side
  if (!side) {
    hoverState.subcategoryId = null
  }
}

const isNarrowLayout = () =>
  window.matchMedia('(max-width: 980px), (max-aspect-ratio: 3/4)').matches

const handleShowcaseHover = (event) => {
  if (isNarrowLayout()) {
    setHoverSide(null)
    return
  }

  const container = event.currentTarget?.querySelector('.container')
  if (!container) {
    setHoverSide(null)
    return
  }

  const rect = container.getBoundingClientRect()
  const pointerX = event.clientX
  const pointerY = event.clientY

  if (pointerX < rect.left) {
    const hoveredSubcategory = [...event.currentTarget.querySelectorAll('.subcategory')].find((el) => {
      const subRect = el.getBoundingClientRect()
      return pointerY >= subRect.top && pointerY <= subRect.bottom
    })

    if (!hoveredSubcategory) {
      setHoverSide(null)
      return
    }

    hoverState.subcategoryId = hoveredSubcategory.dataset.subcategoryId || null
    setHoverSide('left')
    return
  }

  if (pointerX > rect.right) {
    const hoveredSubcategory = [...event.currentTarget.querySelectorAll('.subcategory')].find((el) => {
      const subRect = el.getBoundingClientRect()
      return pointerY >= subRect.top && pointerY <= subRect.bottom
    })

    if (!hoveredSubcategory) {
      setHoverSide(null)
      return
    }

    hoverState.subcategoryId = hoveredSubcategory.dataset.subcategoryId || null
    setHoverSide('right')
    return
  }

  setHoverSide(null)
}

const clearHoverSide = () => {
  setHoverSide(null)
}

const handleShowcaseClick = (event) => {
  if (isNarrowLayout()) return
  if (!hoverState.side || !hoverState.subcategoryId) return

  const container = event.currentTarget?.querySelector('.container')
  if (!container) return

  const rect = container.getBoundingClientRect()
  const pointerX = event.clientX
  const outsideLeft = pointerX < rect.left
  const outsideRight = pointerX > rect.right
  if (!outsideLeft && !outsideRight) return

  if (hoverState.side === 'left') {
    previousProject(hoverState.subcategoryId)
    return
  }

  if (hoverState.side === 'right') {
    nextProject(hoverState.subcategoryId)
  }
}

const toEmbedUrl = (url) => {
  if (!url) return defaultVideoEmbed
  if (url.includes('/embed/')) return url

  const watchMatch = url.match(/[?&]v=([^&]+)/)
  if (watchMatch?.[1]) return `https://www.youtube-nocookie.com/embed/${watchMatch[1]}`

  const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&/]+)/)
  if (shortsMatch?.[1]) return `https://www.youtube-nocookie.com/embed/${shortsMatch[1]}`

  const shortDomainMatch = url.match(/youtu\.be\/([^?&/]+)/)
  if (shortDomainMatch?.[1]) return `https://www.youtube-nocookie.com/embed/${shortDomainMatch[1]}`

  return url
}

const toMapEmbedUrl = (coordinates) =>
  `https://www.google.com/maps?q=${encodeURIComponent(coordinates)}&output=embed`
</script>

<style scoped>
.projects-showcase {
  min-height: 0;
  background: var(--color-bg-dark);
  color: var(--color-light);
  position: relative;
  isolation: isolate;
}

.projects-showcase::before,
.projects-showcase::after {
  content: "";
  position: fixed;
  top: 0;
  bottom: 0;
  width: 50vw;
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
  z-index: 1;
}

.projects-showcase::before {
  left: 0;
  background: linear-gradient(
    to left,
    color-mix(in srgb, var(--color-yellow) 0%, transparent) 0%,
    color-mix(in srgb, var(--color-yellow) 35%, transparent) 58%,
    color-mix(in srgb, var(--color-yellow) 88%, transparent) 100%
  );
}

.projects-showcase::after {
  right: 0;
  background: linear-gradient(
    to right,
    color-mix(in srgb, var(--color-yellow) 0%, transparent) 0%,
    color-mix(in srgb, var(--color-yellow) 35%, transparent) 58%,
    color-mix(in srgb, var(--color-yellow) 88%, transparent) 100%
  );
}

.projects-showcase.show-hover-left::before {
  opacity: 1;
}

.projects-showcase.show-hover-right::after {
  opacity: 1;
}

.container {
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;
  padding: 40px 24px 64px;
  text-align: left;
  position: relative;
  z-index: 2;
}

.category-title {
  margin: 0 0 20px;
  font-family: "Bebas Neue", sans-serif;
  font-size: 50px;
  font-weight: 400;
}

.subcategory {
  position: relative;
  margin-bottom: 30px;
}

.subcategory-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.subcategory-title {
  margin: 0;
  font-family: "Archivo", sans-serif;
  font-size: 26px;
  font-weight: 700;
}

.carousel-controls {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 3;
}

.arrow-btn {
  --arrow-hover-shift: 15%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 72px;
  height: 72px;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  color: var(--color-light);
  cursor: pointer;
  font-size: 62px;
  line-height: 1;
  pointer-events: auto;
}

.arrow-btn:hover {
  color: #9dc7ff;
}

@media (hover: hover) and (pointer: fine) {
  .arrow-btn:hover,
  .arrow-btn.is-area-active {
    color: #9dc7ff;
    animation: arrow-hover-rtl 0.75s ease-in-out infinite;
  }
}

@keyframes arrow-hover-rtl {
  0%,
  100% {
    transform: translateY(-50%) translateX(0);
  }
  50% {
    transform: translateY(-50%) translateX(calc(-1 * var(--arrow-hover-shift)));
  }
}

.arrow-btn-prev {
  left: -98px;
}

.arrow-btn-next {
  right: -98px;
}

.carousel-viewport {
  position: relative;
  overflow: visible;
}

.project-card {
  border: 1px solid rgba(229, 231, 235, 0.45);
  border-radius: 14px;
  background: rgba(14, 24, 36, 0.5);
}

.project-top {
  min-height: 88px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.22);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.project-logo {
  width: auto;
  height: 36px;
  max-width: 150px;
  object-fit: contain;
}

.project-title {
  margin: 0;
  font-family: "Bebas Neue", sans-serif;
  font-size: 38px;
  font-weight: 400;
}

.project-bottom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 320px;
}

.project-info {
  padding: 20px;
  border-right: 1px solid rgba(229, 231, 235, 0.22);
}

.info-title {
  margin: 0 0 10px;
  font-family: "Archivo", sans-serif;
  font-size: 18px;
  font-weight: 700;
}

.info-list {
  margin: 0;
  padding-left: 18px;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 1.5;
}

.project-links {
  margin-top: 16px;
  display: flex;
  gap: 10px;
}

.project-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid rgba(229, 231, 235, 0.45);
  text-decoration: none;
  color: var(--color-light);
  font-family: "Archivo", sans-serif;
  font-size: 13px;
  font-weight: 700;
}

.project-link-btn:hover {
  background: rgba(229, 231, 235, 0.1);
}

.project-video {
  position: relative;
  min-height: 320px;
}

.project-video iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.project-map {
  border-top: 1px solid rgba(229, 231, 235, 0.22);
  overflow: hidden;
}

.project-map-embed {
  width: 100%;
  min-height: 320px;
  border: 0;
  border-radius: 0 0 14px 14px;
  display: block;
}

.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.slide-next-enter-from,
.slide-prev-leave-to {
  opacity: 0;
  transform: translateX(56px);
}

.slide-next-leave-to,
.slide-prev-enter-from {
  opacity: 0;
  transform: translateX(-56px);
}

@media (max-width: 980px), (aspect-ratio <= 3/4) {
  .container {
    max-width: none;
    width: 100%;
    padding-left: 16px;
    padding-right: 16px;
  }

  .category-title {
    font-size: 40px;
  }

  .subcategory-title {
    font-size: 22px;
  }

  .arrow-btn {
    width: 56px;
    height: 56px;
    font-size: 46px;
    position: static;
    transform: none;
  }

  .arrow-btn-prev {
    left: auto;
  }

  .arrow-btn-next {
    right: auto;
  }

  .carousel-controls {
    position: static;
    inset: auto;
    pointer-events: auto;
    z-index: 1;
    margin-top: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .project-top {
    min-height: 72px;
  }

  .project-logo {
    height: 28px;
    max-width: 120px;
  }

  .project-title {
    font-size: 30px;
  }

  .project-bottom {
    grid-template-columns: 1fr;
  }

  .project-info {
    border-right: 0;
    border-bottom: 1px solid rgba(229, 231, 235, 0.22);
  }

  .project-video {
    min-height: 230px;
  }
}
</style>
